import { useState, FormEvent, useEffect, memo, useCallback } from "react";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useAuth } from "../../hooks";
import { ErrorDisplay } from "../common/ErrorDisplay";

/**
 * Enhanced login form with Material UI design
 *
 * Features:
 * - Memoized to prevent unnecessary re-renders
 * - Optimized event handlers
 * - Standardized error display using ErrorDisplay component
 * - Improved form validation
 * - Consistent with other auth forms
 */

const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const {
    login,
    isLoading,
    error: authError,
    requiresMfa,
    clearError,
  } = useAuth();

  // Clear error when form values change
  useEffect(() => {
    if (authError && (email || password)) {
      clearError();
    }
  }, [email, password, authError, clearError]);

  // Handle MFA requirement changes
  useEffect(() => {
    if (requiresMfa) {
      const from = (location.state as any)?.from || { pathname: "/" };
      navigate("/auth/mfa", {
        state: { from },
        replace: true,
      });
    }
  }, [requiresMfa, navigate, location.state]);

  // Validate credentials
  const validateCredentials = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (!email) {
      errors.email = "Email is required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      errors.email = "Invalid email address";
    }

    if (!password) {
      errors.password = "Password is required";
    } else if (password.length < 8) {
      errors.password = "Password must be at least 8 characters";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [email, password]);

  // Memoized form submission handler
  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();

      // Reset validation errors
      setValidationErrors({});

      // Validate credentials
      if (!validateCredentials()) {
        return;
      }

      try {
        await login({ email, password });

        // Don't navigate here - let the parent component handle navigation
        // based on the full authentication state including forcePasswordChange
      } catch (error) {
        // Error is handled in AuthProvider and available via authError
      }
    },
    [login, email, password, validateCredentials],
  );

  // Memoized password visibility toggle
  const toggleShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: "100%" }}>
      <Typography component="h2" variant="h6" align="center" gutterBottom>
        Sign In to Your Account
      </Typography>

      {/* Display authentication errors using ErrorDisplay */}
      {authError && <ErrorDisplay error={authError} onClose={clearError} />}

      <TextField
        margin="normal"
        required
        fullWidth
        id="email"
        label="Email Address"
        name="email"
        autoComplete="email"
        autoFocus
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={!!validationErrors.email}
        helperText={validationErrors.email}
        disabled={isLoading}
      />

      <TextField
        margin="normal"
        required
        fullWidth
        name="password"
        label="Password"
        type={showPassword ? "text" : "password"}
        id="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={!!validationErrors.password}
        helperText={validationErrors.password}
        disabled={isLoading}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={toggleShowPassword}
                edge="end"
                disabled={isLoading}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{ mt: 3, mb: 2 }}
        disabled={isLoading || !email || !password}
      >
        {isLoading ? <CircularProgress size={24} /> : "Sign In"}
      </Button>

      <Box sx={{ textAlign: "center" }}>
        <Link
          component={RouterLink}
          to="/auth/reset-password-request"
          variant="body2"
        >
          Forgot your password?
        </Link>
      </Box>
    </Box>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default memo(LoginForm);
